// A small keyed cache for API data. Screens read with useResource(key, fetcher); writes that change
// server state update the cache with setResource or drop it with invalidate, and every screen
// reading that key re-renders (only those: a reader re-renders when its own key changes).
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

interface Entry {
  data?: unknown
  error?: unknown
  promise?: Promise<unknown>
  /** Bumped on every load or write, so a slow response can't overwrite newer data. */
  version: number
  /** Set by invalidate(); the next reader refetches. */
  stale?: boolean
  /** Bumped on every change a reader can see (a load starting or ending, a write, invalidate). */
  rev: number
}

const entries = new Map<string, Entry>()
const listeners = new Set<() => void>()

function emit(...changed: Entry[]) {
  for (const e of changed) e.rev++
  for (const l of listeners) l()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function load<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const entry = entries.get(key) ?? { version: 0, rev: 0 }
  entries.set(key, entry)
  const version = ++entry.version
  const promise = fetcher().then(
    (data) => {
      if (entry.version === version) { entry.data = data; entry.error = undefined; entry.promise = undefined; emit(entry) }
      return data
    },
    (error) => {
      if (entry.version === version) { entry.error = error; entry.promise = undefined; emit(entry) }
      throw error
    },
  )
  entry.promise = promise
  emit(entry)
  return promise
}

export function setResource<T>(key: string, data: T) {
  const entry = entries.get(key) ?? { version: 0, rev: 0 }
  entry.version++
  entry.data = data
  entry.error = undefined
  entry.promise = undefined
  entries.set(key, entry)
  emit(entry)
}

/** Starts loading a key before any screen reads it (one about to), unless it's cached or on its way. */
export function prefetch<T>(key: string, fetcher: () => Promise<T>) {
  const e = entries.get(key)
  if (!e || (e.data === undefined && e.error === undefined && !e.promise)) load(key, fetcher).catch(() => {})
}

export function getResource<T>(key: string): T | undefined {
  return entries.get(key)?.data as T | undefined
}

/** Drops cached data whose key starts with any of the prefixes; mounted readers refetch. */
export function invalidate(...prefixes: string[]) {
  const dropped: Entry[] = []
  for (const [key, e] of entries) {
    if (prefixes.some((p) => key.startsWith(p))) {
      e.version++
      e.promise = undefined
      e.stale = true
      dropped.push(e)
    }
  }
  if (dropped.length) emit(...dropped)
}

export interface Resource<T> {
  readonly data: T | undefined
  readonly error: unknown
  readonly loading: boolean
  reload(): Promise<T>
}

/**
 * `revalidate`: fetch once more when this reader mounts (or its key changes), showing what's cached
 * until the answer comes; for data other players change, like challenges.
 */
export function useResource<T>(key: string | null, fetcher: () => Promise<T>, options: { revalidate?: boolean } = {}): Resource<T> {
  useSyncExternalStore(subscribe, () => (key ? (entries.get(key)?.rev ?? -1) : 0))
  const entry = key ? entries.get(key) : undefined
  const revalidated = useRef<string | null>(null)

  useEffect(() => {
    if (!key) return
    const e = entries.get(key)
    if (options.revalidate && revalidated.current !== key) {
      revalidated.current = key
      // A fetch already on its way (another reader's) is fresh enough.
      if (e && !e.promise) e.stale = true
    }
    if (!e || e.stale || (e.data === undefined && e.error === undefined && !e.promise)) {
      if (e) e.stale = false
      load(key, fetcher).catch(() => {})
    }
  })

  const reload = useCallback(() => (key ? load(key, fetcher) : Promise.reject(new Error('no key'))), [key, fetcher])
  return {
    data: entry?.data as T | undefined,
    error: entry?.error,
    loading: !!key && (!entry || entry.promise !== undefined || entry.stale === true),
    reload,
  }
}
